import React, { useEffect, useState } from "react";
import { withRouter } from "../../../utils/withRouter";
import api from "../../../const/axios";
import { CheckCircle, XCircle, Clock, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";

interface Props {
  params: { token: string };
}

type PageState = "loading" | "ready" | "already_responded" | "request_closed" | "submitting" | "done_approved" | "done_declined" | "error";

const VerifyReference: React.FC<Props> = ({ params }) => {
  const [state, setState] = useState<PageState>("loading");
  const [info, setInfo] = useState<any>(null);
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    api.get(`verification-requests/reference/${params.token}`)
      .then((res) => {
        setInfo(res.data);
        if (res.data.status !== "Pending" || res.data.requestStatus !== "PendingReferences") {
          setState(res.data.status !== "Pending" ? "already_responded" : "request_closed");
        } else {
          setState("ready");
        }
      })
      .catch(() => setState("error"));
  }, [params.token]);

  const respond = async (approve: boolean) => {
    setState("submitting");
    try {
      await api.put(`verification-requests/reference/${params.token}/respond`, { approve });
      setState(approve ? "done_approved" : "done_declined");
    } catch (e: any) {
      setErrorMsg(e?.response?.data?.message ?? "Xəta baş verdi.");
      setState("error");
    }
  };

  if (state === "loading") {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (state === "error") {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 shadow-sm p-8 text-center space-y-3">
          <AlertCircle className="w-12 h-12 text-red-400 mx-auto" />
          <p className="font-bold text-gray-900 dark:text-gray-50 text-lg">Keçid tapılmadı</p>
          <p className="text-sm text-gray-500 dark:text-gray-400">{errorMsg || "Bu keçid mövcud deyil və ya artıq istifadə edilib."}</p>
        </div>
      </div>
    );
  }

  if (state === "already_responded") {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 shadow-sm p-8 text-center space-y-3">
          <Clock className="w-12 h-12 text-amber-400 mx-auto" />
          <p className="font-bold text-gray-900 dark:text-gray-50 text-lg">Artıq cavab vermisiniz</p>
          <p className="text-sm text-gray-500 dark:text-gray-400">Bu müraciətə artıq cavab vermişdiniz.</p>
        </div>
      </div>
    );
  }

  if (state === "request_closed") {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 shadow-sm p-8 text-center space-y-3">
          <AlertCircle className="w-12 h-12 text-gray-400 mx-auto" />
          <p className="font-bold text-gray-900 dark:text-gray-50 text-lg">Müraciət bağlanıb</p>
          <p className="text-sm text-gray-500 dark:text-gray-400">Bu müraciət artıq aktiv deyil.</p>
        </div>
      </div>
    );
  }

  if (state === "done_approved") {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 shadow-sm p-8 text-center space-y-3">
          <CheckCircle className="w-12 h-12 text-green-500 mx-auto" />
          <p className="font-bold text-gray-900 dark:text-gray-50 text-lg">Təsdiqlədiniz</p>
          <p className="text-sm text-gray-500 dark:text-gray-400">Zəmanətiniz qeydə alındı. Təşəkkür edirik!</p>
        </div>
      </div>
    );
  }

  if (state === "done_declined") {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 shadow-sm p-8 text-center space-y-3">
          <XCircle className="w-12 h-12 text-red-400 mx-auto" />
          <p className="font-bold text-gray-900 dark:text-gray-50 text-lg">Rədd etdiniz</p>
          <p className="text-sm text-gray-500 dark:text-gray-400">Cavabınız qeydə alındı.</p>
        </div>
      </div>
    );
  }

  // state === "ready" | "submitting"
  const applicant = info?.applicant;
  const referee = info?.referee;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 flex items-center justify-center px-4 pt-16">
      <div className="max-w-md w-full bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 shadow-sm p-8 space-y-6">
        <div className="text-center">
          <p className="text-xs font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wide mb-1">Zəmanət müraciəti</p>
          <p className="font-bold text-gray-900 dark:text-gray-50 text-xl">
            {applicant?.name} {applicant?.surname}
          </p>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            sizi zəmanətçi olaraq göstərmişdir
          </p>
        </div>

        {applicant?.note && (
          <div className="bg-gray-50 dark:bg-gray-800 rounded-xl px-4 py-3">
            <p className="text-xs font-semibold text-gray-400 dark:text-gray-500 mb-1">Müraciət notu</p>
            <p className="text-sm text-gray-700 dark:text-gray-200">{applicant.note}</p>
          </div>
        )}

        <div className="text-sm text-gray-500 dark:text-gray-400 text-center">
          Salam, <span className="font-semibold text-gray-700 dark:text-gray-200">{referee?.name} {referee?.surname}</span>.<br />
          Bu şəxsi tanıyırsınızsa, zəmanət verin.
        </div>

        <div className="flex gap-3">
          <Button
            variant="outline"
            className="flex-1 gap-2 border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700 dark:border-red-900 dark:text-red-400 dark:hover:bg-red-950"
            disabled={state === "submitting"}
            onClick={() => respond(false)}
          >
            <XCircle className="w-4 h-4" />
            Rədd et
          </Button>
          <Button
            className="flex-1 gap-2 bg-green-600 hover:bg-green-700"
            disabled={state === "submitting"}
            onClick={() => respond(true)}
          >
            <CheckCircle className="w-4 h-4" />
            Zəmanət ver
          </Button>
        </div>
      </div>
    </div>
  );
};

export default withRouter(VerifyReference);
